import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orderErrorMessage } from '../../../entities/order';
import { removeScript } from '../../../entities/script-analysis';
import { supabase } from '../../../shared/api/supabase';
import { Button } from '../../../shared/ui/button';
import { ConfirmDialog } from '../../../shared/ui/confirm-dialog';
import { toast } from '../../../shared/ui/sonner';

/** Everything that shows a script, its analysis or what was read from it. */
const SCRIPT_QUERIES = ['script-analysis', 'script-project', 'project-file', 'order', 'projects'];

/** Removes the project's script through the worker and refreshes what showed it. */
export function useRemoveScript(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('forbidden');
      await removeScript(token, projectId);
    },
    onSuccess: () =>
      Promise.all(SCRIPT_QUERIES.map((key) => qc.invalidateQueries({ queryKey: [key] }))),
  });
}

/**
 * **Replace script** and **Remove script**. Both delete the script, its word count and what was read from it,
 * behind a confirmation that says the form will be filled in again from the next file; the page then returns to
 * the upload step. `before` lets the form finish saving first.
 */
export function ScriptActions({
  projectId,
  before,
}: {
  projectId: string;
  before?: () => Promise<unknown>;
}) {
  const { t } = useTranslation('order');
  const remove = useRemoveScript(projectId);
  const [confirming, setConfirming] = useState<'replace' | 'remove' | null>(null);
  const replacing = confirming === 'replace';
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="secondary" size="sm" onClick={() => setConfirming('replace')}>
        {t('script.file.replace')}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setConfirming('remove')}>
        {t('script.file.remove')}
      </Button>
      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={replacing ? t('script.file.replaceTitle') : t('script.file.removeTitle')}
        description={replacing ? t('script.file.replaceBody') : t('script.file.removeBody')}
        confirmLabel={replacing ? t('script.file.replaceYes') : t('script.file.removeYes')}
        cancelLabel={t('script.file.keep')}
        errorMessage={orderErrorMessage}
        onConfirm={async () => {
          await before?.();
          await remove.mutateAsync();
          toast.success(t('script.file.removed'));
        }}
      />
    </div>
  );
}
